import { useEffect, useRef, useState, useCallback } from "react";
import { Button, Drawer, Empty, Form, Input, List, Modal, Popconfirm, Select, Space, Steps, Table, Tabs, Tag, Typography, message } from "antd";
import { DeleteOutlined, KeyOutlined, PlusOutlined, CloudOutlined, SafetyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useManagementStore } from "@/stores/management";
import { useAuthStore } from "@/stores/auth";
import { providerService, type ProviderInfo } from "@/services/provider";
import { clientService, type ClientInfo } from "@/services/client";
import { orgService, type RoleInfo, type MemberInfo } from "@/services/organization";
import { providerAccessService, type RoleAccess, type UserAccess } from "@/services/providerAccess";
import { useCardExtra } from "@/layouts/ManagementLayout";
import { formatDateTime } from "@/utils/date";

const { Text } = Typography;

type AuthStep = "select" | "naming" | "waiting" | "need_code" | "success" | "failed";

export function ProviderManagement() {
  const { t } = useTranslation("management");
  const { context, hasPermission, permissions } = useManagementStore();
  const { accessToken } = useAuthStore();
  const orgId = context?.type === "org" ? context.org.id : "";
  const canManage = hasPermission("ProviderCreate");

  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [clients, setClients] = useState<ClientInfo[]>([]);
  const [loading, setLoading] = useState(true);

  // OAuth auth flow state
  const [oauthOpen, setOauthOpen] = useState(false);
  const [authStep, setAuthStep] = useState<AuthStep>("select");
  const [authAdapterType, setAuthAdapterType] = useState("ClaudeCode");
  const [authClientId, setAuthClientId] = useState("");
  const [authName, setAuthName] = useState("");
  const [authProviderId, setAuthProviderId] = useState("");
  const [loginUrl, setLoginUrl] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [authMessage, setAuthMessage] = useState("");
  const wsRef = useRef<WebSocket | null>(null);

  // Access management state
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessProvider, setAccessProvider] = useState<ProviderInfo | null>(null);
  const [roleAccesses, setRoleAccesses] = useState<RoleAccess[]>([]);
  const [userAccesses, setUserAccesses] = useState<UserAccess[]>([]);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [addRoleId, setAddRoleId] = useState<string>("");
  const [addRoleModels, setAddRoleModels] = useState<string>("");
  const [addUserId, setAddUserId] = useState<string>("");
  const [addUserModels, setAddUserModels] = useState<string>("");

  // ApiProxy create state
  const [apiOpen, setApiOpen] = useState(false);
  const [apiForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [p, c] = await Promise.all([providerService.list(orgId), clientService.list(orgId)]);
      setProviders(p);
      setClients(c);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [orgId]);

  useCardExtra(
    canManage ? (
      <span style={{ display: "flex", gap: 8 }}>
        <Button size="small" icon={<KeyOutlined />} onClick={() => { setAuthStep("select"); setOauthOpen(true); }}>
          OAuth
        </Button>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { apiForm.resetFields(); setApiOpen(true); }}>
          API Key
        </Button>
      </span>
    ) : null,
    [t, permissions],
  );

  // ── OAuth WS auth flow ──

  const startOAuth = () => {
    const pid = crypto.randomUUID();
    setAuthProviderId(pid);
    setAuthStep("waiting");
    setLoginUrl("");
    setAuthCode("");
    setAuthMessage("");

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws/user?token=${encodeURIComponent(accessToken || "")}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      const msg = JSON.stringify({
        msg_id: crypto.randomUUID(),
        payload: {
          type: "StartAuth",
          data: { provider_id: pid, adapter_type: authAdapterType, name: authName, client_id: authClientId },
        },
      });
      ws.send(msg);
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        const payload = msg.payload;
        if (payload?.type === "AuthEvent" && payload?.data) {
          // serde format: event = { type: "LoginUrl", data: "https://..." }
          const event = payload.data.event;
          const eventType = typeof event === "string" ? event : event?.type;
          const eventData = typeof event === "object" ? event?.data : undefined;

          switch (eventType) {
            case "LoginUrl":
              setLoginUrl(eventData || "");
              setAuthStep("need_code");
              break;
            case "NeedCode":
              setAuthStep("need_code");
              break;
            case "Success":
              setAuthStep("success");
              load();
              break;
            case "Failed":
              setAuthMessage(eventData || "Authentication failed");
              setAuthStep("failed");
              break;
            case "Info":
              setAuthMessage(eventData || "");
              break;
          }
        }
      } catch { /* ignore */ }
    };

    ws.onerror = () => { setAuthStep("failed"); setAuthMessage("WebSocket connection failed"); };
    ws.onclose = () => { wsRef.current = null; };
  };

  const submitCode = () => {
    if (wsRef.current && authCode.trim()) {
      wsRef.current.send(JSON.stringify({
        msg_id: crypto.randomUUID(),
        payload: { type: "SubmitAuthCode", data: { provider_id: authProviderId, code: authCode.trim() } },
      }));
      setAuthStep("waiting");
    }
  };

  const closeOAuth = () => {
    wsRef.current?.close();
    setOauthOpen(false);
  };

  // ── ApiProxy create ──

  const handleCreateApi = async () => {
    const values = await apiForm.validateFields();
    setSaving(true);
    try {
      await providerService.createApiProxy(orgId, values);
      setApiOpen(false);
      message.success(t("common:success"));
      load();
    } catch { message.error(t("common:error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (pid: string) => {
    try {
      await providerService.delete(orgId, pid);
      message.success(t("common:success"));
      load();
    } catch { message.error(t("common:error")); }
  };

  // ── Access management ──

  const loadAccessData = useCallback(async (providerId: string) => {
    if (!orgId) return;
    setAccessLoading(true);
    try {
      const [ra, ua, ro, me] = await Promise.all([
        providerAccessService.listRoleAccess(orgId),
        providerAccessService.listUserAccess(orgId),
        orgService.listRoles(orgId),
        orgService.listMembers(orgId),
      ]);
      setRoleAccesses(ra.filter((a) => a.provider_id === providerId));
      setUserAccesses(ua.filter((a) => a.provider_id === providerId));
      setRoles(ro);
      setMembers(me);
    } catch { /* empty */ }
    finally { setAccessLoading(false); }
  }, [orgId]);

  const openAccessDrawer = (provider: ProviderInfo) => {
    setAccessProvider(provider);
    setAccessOpen(true);
    setAddRoleId("");
    setAddRoleModels("");
    setAddUserId("");
    setAddUserModels("");
    loadAccessData(provider.id);
  };

  const handleAddRoleAccess = async () => {
    if (!orgId || !accessProvider || !addRoleId) return;
    try {
      const models = addRoleModels.trim() ? addRoleModels.split(",").map((m) => m.trim()).filter(Boolean) : ["*"];
      await providerAccessService.createRoleAccess(orgId, {
        role_id: addRoleId,
        provider_id: accessProvider.id,
        allowed_models: models,
      });
      message.success(t("common:success"));
      setAddRoleId("");
      setAddRoleModels("");
      loadAccessData(accessProvider.id);
    } catch { message.error(t("common:error")); }
  };

  const handleDeleteRoleAccess = async (accessId: string) => {
    if (!orgId || !accessProvider) return;
    try {
      await providerAccessService.deleteRoleAccess(orgId, accessId);
      message.success(t("common:success"));
      loadAccessData(accessProvider.id);
    } catch { message.error(t("common:error")); }
  };

  const handleAddUserAccess = async () => {
    if (!orgId || !accessProvider || !addUserId) return;
    try {
      const models = addUserModels.trim() ? addUserModels.split(",").map((m) => m.trim()).filter(Boolean) : ["*"];
      await providerAccessService.createUserAccess(orgId, {
        user_id: addUserId,
        provider_id: accessProvider.id,
        allowed_models: models,
      });
      message.success(t("common:success"));
      setAddUserId("");
      setAddUserModels("");
      loadAccessData(accessProvider.id);
    } catch { message.error(t("common:error")); }
  };

  const handleDeleteUserAccess = async (accessId: string) => {
    if (!orgId || !accessProvider) return;
    try {
      await providerAccessService.deleteUserAccess(orgId, accessId);
      message.success(t("common:success"));
      loadAccessData(accessProvider.id);
    } catch { message.error(t("common:error")); }
  };

  const roleMap = Object.fromEntries(roles.map((r) => [r.id, r.name]));
  const memberMap = Object.fromEntries(members.map((m) => [m.user_id, m.user_name || m.user_account || m.user_id.slice(0, 8)]));

  // Roles not yet assigned to this provider
  const availableRoles = roles.filter((r) => !roleAccesses.some((a) => a.role_id === r.id));
  // Members not yet assigned to this provider
  const availableMembers = members.filter((m) => !userAccesses.some((a) => a.user_id === m.user_id));

  const onlineClients = clients.filter((c) => c.status === "online");
  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c.name]));

  const columns = [
    { title: t("providers.table.name"), dataIndex: "name", key: "name" },
    {
      title: t("providers.table.type"), dataIndex: "provider_type", key: "type",
      render: (v: string) => <Tag color={v === "OAuth" ? "blue" : "green"}>{v}</Tag>,
    },
    { title: t("providers.table.adapter"), dataIndex: "adapter_type", key: "adapter" },
    {
      title: t("providers.table.client"), dataIndex: "client_id", key: "client",
      render: (v: string | null) => v ? (clientMap[v] || v.slice(0, 8)) : "—",
    },
    {
      title: t("providers.table.status"), dataIndex: "status", key: "status",
      render: (s: string) => {
        const color = s === "active" ? "green" : s === "pending" ? "orange" : s === "error" ? "red" : "default";
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: t("providers.table.created_at"), dataIndex: "created_at", key: "created_at",
      render: (v: string) => formatDateTime(v),
    },
    ...(canManage ? [{
      title: t("common:action"), key: "action", width: 120,
      render: (_: unknown, record: ProviderInfo) => (
        <Space size={0}>
          <Button type="text" icon={<SafetyOutlined />} size="small" onClick={() => openAccessDrawer(record)} />
          <Popconfirm title={t("providers.delete.confirm")} onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  const stepItems = [
    { title: t("providers.oauth.step_select") },
    { title: t("providers.oauth.step_auth") },
    { title: t("providers.oauth.step_done") },
  ];
  const currentStep = authStep === "select" ? 0 : (authStep === "success" || authStep === "failed") ? 2 : 1;

  return (
    <>
      <Table dataSource={providers} columns={columns} rowKey="id" loading={loading} size="middle" />

      {/* OAuth Auth Modal */}
      <Modal
        title="OAuth Provider"
        open={oauthOpen}
        onCancel={closeOAuth}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Steps current={currentStep} items={stepItems} size="small" style={{ marginBottom: 24 }} />

        {authStep === "select" && (
          <Form layout="vertical">
            <Form.Item label="Adapter Type">
              <Select value={authAdapterType} onChange={setAuthAdapterType}
                options={[{ label: "Claude Code", value: "ClaudeCode" }, { label: "Codex", value: "Codex" }]} />
            </Form.Item>
            <Form.Item label={t("providers.table.client")}>
              <Select value={authClientId} onChange={setAuthClientId} placeholder="Select online client"
                options={onlineClients.map((c) => ({ label: c.name, value: c.id }))} />
            </Form.Item>
            <Form.Item label={t("providers.table.name")}>
              <Input value={authName} onChange={(e) => setAuthName(e.target.value)} placeholder="e.g. Claude OAuth" />
            </Form.Item>
            <Button type="primary" block disabled={!authClientId || !authName} onClick={startOAuth}>
              {t("providers.oauth.start")}
            </Button>
          </Form>
        )}

        {authStep === "waiting" && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <CloudOutlined style={{ fontSize: 40, color: "var(--primary-color)" }} />
            <div style={{ marginTop: 16 }}>{t("providers.oauth.waiting")}</div>
            {authMessage && <Text type="secondary">{authMessage}</Text>}
          </div>
        )}

        {authStep === "need_code" && (
          <div>
            {loginUrl && (
              <div style={{ marginBottom: 16 }}>
                <Text>{t("providers.oauth.open_url")}</Text>
                <div style={{ marginTop: 8 }}>
                  <a href={loginUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: "break-all" }}>{loginUrl}</a>
                </div>
              </div>
            )}
            {authMessage && (
              <div style={{ marginBottom: 16, padding: "8px 12px", background: "var(--content-bg)", borderRadius: 6, fontFamily: "monospace", fontSize: 16, textAlign: "center", fontWeight: 600 }}>
                {authMessage}
              </div>
            )}
            <Input.Search
              placeholder={t("providers.oauth.paste_code")}
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
              enterButton={t("common:confirm")}
              onSearch={submitCode}
            />
          </div>
        )}

        {authStep === "success" && (
          <div style={{ textAlign: "center", padding: 40, color: "var(--success)" }}>
            <div style={{ fontSize: 40 }}>✓</div>
            <div style={{ marginTop: 16 }}>{t("providers.oauth.success")}</div>
          </div>
        )}

        {authStep === "failed" && (
          <div style={{ textAlign: "center", padding: 40, color: "var(--error)" }}>
            <div style={{ fontSize: 40 }}>✗</div>
            <div style={{ marginTop: 16 }}>{authMessage || t("providers.oauth.failed")}</div>
          </div>
        )}
      </Modal>

      {/* ApiProxy Create Modal */}
      <Modal title="API Key Provider" open={apiOpen} onOk={handleCreateApi} onCancel={() => setApiOpen(false)} confirmLoading={saving}>
        <Form form={apiForm} layout="vertical">
          <Form.Item label={t("providers.table.name")} name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Adapter Type" name="adapter_type" rules={[{ required: true }]}>
            <Select options={[{ label: "Claude Code", value: "ClaudeCode" }, { label: "Codex", value: "Codex" }]} />
          </Form.Item>
          <Form.Item label="Base URL" name="base_url" rules={[{ required: true }]}>
            <Input placeholder="https://api.anthropic.com" />
          </Form.Item>
          <Form.Item label="API Key" name="api_key" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item label="Default Model" name="default_model">
            <Input placeholder="claude-sonnet-4-20250514" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Access Management Drawer */}
      <Drawer
        title={`${accessProvider?.name ?? ""} — ${t("providers.access.title")}`}
        open={accessOpen}
        onClose={() => setAccessOpen(false)}
        width={520}
        destroyOnClose
      >
        <Tabs
          defaultActiveKey="role"
          items={[
            {
              key: "role",
              label: t("providers.access.role_tab"),
              children: (
                <>
                  {roleAccesses.length === 0 && !accessLoading && (
                    <Empty description={t("providers.access.no_access")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                  <List
                    loading={accessLoading}
                    dataSource={roleAccesses}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Popconfirm
                            key="del"
                            title={t("providers.access.delete_confirm")}
                            onConfirm={() => handleDeleteRoleAccess(item.id)}
                          >
                            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          title={roleMap[item.role_id] || item.role_id.slice(0, 8)}
                          description={
                            item.allowed_models.includes("*")
                              ? <Tag color="blue">{t("providers.access.all_models")}</Tag>
                              : item.allowed_models.map((m) => <Tag key={m}>{m}</Tag>)
                          }
                        />
                      </List.Item>
                    )}
                  />
                  {canManage && (
                    <div style={{ marginTop: 16, padding: "12px 0", borderTop: "1px solid var(--border-color, #f0f0f0)" }}>
                      <Text strong style={{ display: "block", marginBottom: 8 }}>{t("providers.access.add_role")}</Text>
                      <Space.Compact style={{ width: "100%" }}>
                        <Select
                          style={{ width: "40%" }}
                          value={addRoleId || undefined}
                          onChange={setAddRoleId}
                          placeholder={t("providers.access.select_role")}
                          options={availableRoles.map((r) => ({ label: r.name, value: r.id }))}
                        />
                        <Input
                          style={{ width: "40%" }}
                          value={addRoleModels}
                          onChange={(e) => setAddRoleModels(e.target.value)}
                          placeholder={t("providers.access.models_placeholder")}
                        />
                        <Button type="primary" icon={<PlusOutlined />} disabled={!addRoleId} onClick={handleAddRoleAccess} />
                      </Space.Compact>
                    </div>
                  )}
                </>
              ),
            },
            {
              key: "user",
              label: t("providers.access.user_tab"),
              children: (
                <>
                  {userAccesses.length === 0 && !accessLoading && (
                    <Empty description={t("providers.access.no_access")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                  <List
                    loading={accessLoading}
                    dataSource={userAccesses}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Popconfirm
                            key="del"
                            title={t("providers.access.delete_confirm")}
                            onConfirm={() => handleDeleteUserAccess(item.id)}
                          >
                            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          title={memberMap[item.user_id] || item.user_id.slice(0, 8)}
                          description={
                            item.allowed_models.includes("*")
                              ? <Tag color="blue">{t("providers.access.all_models")}</Tag>
                              : item.allowed_models.map((m) => <Tag key={m}>{m}</Tag>)
                          }
                        />
                      </List.Item>
                    )}
                  />
                  {canManage && (
                    <div style={{ marginTop: 16, padding: "12px 0", borderTop: "1px solid var(--border-color, #f0f0f0)" }}>
                      <Text strong style={{ display: "block", marginBottom: 8 }}>{t("providers.access.add_user")}</Text>
                      <Space.Compact style={{ width: "100%" }}>
                        <Select
                          style={{ width: "40%" }}
                          value={addUserId || undefined}
                          onChange={setAddUserId}
                          placeholder={t("providers.access.select_user")}
                          options={availableMembers.map((m) => ({
                            label: m.user_name || m.user_account || m.user_id.slice(0, 8),
                            value: m.user_id,
                          }))}
                        />
                        <Input
                          style={{ width: "40%" }}
                          value={addUserModels}
                          onChange={(e) => setAddUserModels(e.target.value)}
                          placeholder={t("providers.access.models_placeholder")}
                        />
                        <Button type="primary" icon={<PlusOutlined />} disabled={!addUserId} onClick={handleAddUserAccess} />
                      </Space.Compact>
                    </div>
                  )}
                </>
              ),
            },
          ]}
        />
      </Drawer>
    </>
  );
}
